import { useEffect, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { loadProfileSettings, updateProfile } from '@/modules/settings/services';
import { getInitialProfileForm } from '@/modules/settings/utils';

export function useProfileSettings() {
  const { currentUser, isAuthenticated, updateCurrentUserEmail } = useSession();
  const [profile, setProfile] = useState(getInitialProfileForm(currentUser));
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profileMissing, setProfileMissing] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      if (!currentUser) {
        setProfile(getInitialProfileForm(currentUser));
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      const result = await loadProfileSettings();
      if (!active) return;
      const baseProfile = getInitialProfileForm(result.data?.authUser || currentUser);
      const staffProfile = result.data?.staffProfile;
      setProfile({
        ...baseProfile,
        phone: staffProfile?.phone || '',
        professionalTitle: staffProfile?.role || '',
        staffType: staffProfile?.tipoPersonale || 'interno',
        skills: Array.isArray(staffProfile?.skills) ? staffProfile.skills.join(', ') : '',
      });
      setProfileMissing(result.data?.staffProfileMissing === true);
      setStatusMessage(result.data?.staffError || '');
      setIsEditing(result.data?.staffProfileMissing === true);
      setIsLoading(false);
    }
    load();
    return () => {
      active = false;
    };
  }, [currentUser]);

  function updateField(field, value) {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function saveProfile() {
    if (!currentUser) return;

    const nextEmail = (profile.email || '').trim().toLowerCase();
    const currentEmail = (currentUser.email || '').trim().toLowerCase();
    if (!nextEmail) {
      setStatusMessage('Inserisci un indirizzo email valido.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');

    if (nextEmail !== currentEmail) {
      try {
        await updateCurrentUserEmail(nextEmail);
      } catch (error) {
        setStatusMessage(error.message || 'Email non aggiornata.');
        setIsSaving(false);
        return;
      }
    }

    const result = await updateProfile({
      fullName: profile.fullName,
      name: profile.fullName,
      email: nextEmail,
      phone: profile.phone,
      role: profile.professionalTitle,
      tipoPersonale: profile.staffType,
      skills: profile.skills,
    });
    if (!result.ok) {
      setStatusMessage(result.message);
      setIsSaving(false);
      return;
    }

    setStatusMessage(result.message);
    setProfileMissing(false);
    setIsEditing(false);
    setIsSaving(false);
  }

  function cancelEditing() {
    setProfile(getInitialProfileForm(currentUser));
    setIsEditing(false);
  }

  return {
    profile,
    currentUser,
    isAuthenticated,
    isEditing,
    isLoading,
    isSaving,
    profileMissing,
    statusMessage,
    setIsEditing,
    updateField,
    saveProfile,
    cancelEditing,
  };
}
